import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { capitalize, cloneDeep, isEmpty, set, times } from 'lodash'
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs'
import useForm from '@teqplay/react-useform'
import moment from 'moment'
import { toast } from 'react-toastify'

import InputTextField from '../../../fields/inputtextfield/InputTextField'
import InputDateField from '../../../fields/inputdatefield/InputDateField'
import InputNumberField from '../../../fields/inputnumberfield/InputNumberField'
import CheckBoxField from '../../../fields/checkboxfield/CheckBoxField'
import DisplayField from '../../../fields/displayfield/DisplayField'
import {
  deleteCargoOperationsDetailsFromBackendRest,
  downloadCargoManifest,
  fetchCargoOperationsDetailsFromBackendRest,
  insertCargoOperationsDetailsInBackendRest,
  sendCargoOperationsEmail,
  updateCargoOperationsDetailsInBackendRest
} from '../../../../js/rest'
import { validateCargoOperations, getErrorMessageForFormField, checkIfAFieldIsEmpty } from '../../../../utils/formUtils'
import {
  calculateAverageUnloadSpeed,
  calculateEndUnloading,
  calculateLevelDifference,
  calculateMass,
  calculateMeasurementDensity,
  calculateStartUnloading,
  calculateSummary,
  roundIfDefined
} from '../../../../utils/cargoOperationsUtils'
import InputSelectField from '../../../fields/inputselectfield/InputSelectField'
import { confirmPopup } from '../../../popup/confirmPopup'
import {
  CARGO_DETAILS_TYPE,
  CARGO_OPERATIONS_EMAIL_ADDRESSES,
  CARGO_OPERATIONS_REPLY_TO_EMAIL,
  startDateFormatString
} from '../../../../js/constants'
import { alertPopup } from '../../../popup/alertPopup'
import InputTextAreaField from '../../../fields/inputtextareafield/InputTextAreaField'
import { handleFetchError } from '../../../../js/sentry'
import { confirmDeletePopup } from '../../../popup/confirmDeletePopup'
import Modal from '../../../app/customModal/Modal'
import ErrorMessage from '../../../errormessage/ErrorMessage'
import ApproveVesselPopup from '../../approveVesselPopup/ApproveVesselPopup'

import 'react-tabs/style/react-tabs.css'
import './CargoOperationsDetailsCargill.scss'

const MEASUREMENTS_TANK_OPTIONS = ['']
  .concat(times(9, index => 'Backboard ' + (index + 1)))
  .concat(times(9, index => 'Starboard ' + (index + 1)))
  .concat(times(9, index => 'Center ' + (index + 1)))

const STATUS_CLASS_NAME = {
  true: 'ok',
  false: 'not-ok',
  undefined: ''
}

const apiUrlSuffix = CARGO_DETAILS_TYPE.cargill
const initialCargoOperationDetails = {}

function CargoOperationsDetailsCargill(props) {
  const [createNew, setCreateNew] = useState(false)
  const [approveVesselPopupActive, setApproveVesselPopupActive] = useState(false)
  const [tabIndex, setTabIndex] = useState(0)
  const [hasBeenValidatedOnce, setHasBeenValidatedOnce] = useState(false)

  // Check if form should validate a cargo manifest. Default is false
  const validateManifest = React.useRef(false)

  const { values, errors, handleSubmit, setAllValues, triggerValidation } = useForm({
    initialValues: initialCargoOperationDetails,
    validate,
    onValidSubmit
  })

  const {
    readOnly,
    identifiers: { terminalVisitId }
  } = props

  useEffect(() => {
    if (terminalVisitId) {
      // Fetch the terminal visit from the backend
      fetchCargoOperationsDetails()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terminalVisitId])

  useEffect(() => {
    if (hasBeenValidatedOnce) {
      triggerValidation()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values])

  const {
    billOfLoadingDate,
    deliveredSamples,
    guidelinesSubmitted,
    requiredDocumentsReceived,
    densityDuringLoading,
    temperatureDuringLoading,
    bargeMeasurementDuringLoading,
    arrivalTime,
    releaseTimeLab,
    dateOfAttestationOfUnloading,
    unloadingDetails,
    measurements,
    seals,
    emptyBargeInspection,
    dischargePointClosedInspection,
    afterUnloadingComment,
    slobTanks,
    statusComment,
    approved,
    approvedBy,
    approvedTimestamp
  } = values

  const {
    // general
    dateOfArrival,
    shipName,
    imo,
    eni,
    pNumber,
    product,

    // loading
    billOfLoadingWeight,
    weightDifferenceAtLoading,
    weightDifferenceAtLoadingOk,

    // Izegem
    totalMeasuredVolume,
    averageMeasuredTemperature,
    totalMeasuredWeight,
    weightDifference,
    weightDifferenceOk,
    weightDifferenceIzegem,
    weightDifferenceIzegemOk,
    allDifferencesOk
  } = calculateSummary(props.order, values, props.ship)

  const statusOk = allDifferencesOk === true || approved === true
  const statusOkText = statusOk === true ? 'RELEASED' : statusOk === false ? 'BLOCKED' : undefined

  return (
    <>
      <Modal.Wrapper
        isOpen={approveVesselPopupActive}
        title="Approve blocked vessel"
        onCloseModal={toggleApproveVesselPopup}
      >
        <ApproveVesselPopup approveBlockedVessel={approveBlockedVessel} />
      </Modal.Wrapper>

      <div className="content-wrapper cargo-details-cargill">
        <Tabs selectedIndex={tabIndex} onSelect={tabIndex => setTabIndex(tabIndex)}>
          <TabList
            className={
              // override tabs styling on TerminalVisitForm
              ['react-tabs__tab-list', 'cargo-details-tablist']
            }
          >
            <Tab>General information</Tab>
            <Tab>Measurements</Tab>
            <Tab>Unloading details</Tab>
            <Tab>After unloading</Tab>
            <Tab>Summary</Tab>
          </TabList>
          <TabPanel>
            <div className="detail-view content columns">
              <div className="detail-view-section">
                <div className="detail-view">
                  <InputTextField
                    name={'P Number'}
                    fieldName={'pNumber'}
                    fieldValue={pNumber}
                    error={false}
                    required={false}
                    onChange={onFormInputChange}
                    readOnly={readOnly}
                  />
                </div>
                <div className="detail-view">
                  <InputDateField
                    name={'Bill of loading date'}
                    fieldName={'billOfLoadingDate'}
                    fieldValue={billOfLoadingDate}
                    error={getErrorMessageForFormField(errors, 'billOfLoadingDate')}
                    required={true}
                    onChange={onFormInputChange}
                    readOnly={readOnly}
                  />
                </div>
                <div className="detail-view">
                  <InputNumberField
                    name={'Bill of loading weight (kg)'}
                    fieldName={'billOfLoadingWeight'}
                    fieldValue={billOfLoadingWeight}
                    required={false}
                    error={getErrorMessageForFormField(errors, 'billOfLoadingWeight')}
                    onChange={onFormInputChange}
                    readOnly={readOnly}
                  />
                </div>
                <div className="detail-view">
                  <InputNumberField
                    name={'Delivered samples'}
                    fieldName={'deliveredSamples'}
                    fieldValue={deliveredSamples}
                    required={false}
                    error={getErrorMessageForFormField(errors, 'deliveredSamples')}
                    onChange={onFormInputChange}
                    readOnly={readOnly}
                  />
                </div>
                <div className="detail-view">
                  <CheckBoxField
                    name={'Guidelines submitted'}
                    fieldName={'guidelinesSubmitted'}
                    fieldValue={guidelinesSubmitted}
                    onChange={onFormInputChange}
                    readOnly={readOnly}
                  />
                </div>
                <div className="detail-view">
                  <CheckBoxField
                    name={'Required documents received'}
                    fieldName={'requiredDocumentsReceived'}
                    fieldValue={requiredDocumentsReceived}
                    onChange={onFormInputChange}
                    readOnly={readOnly}
                  />
                </div>
              </div>

              <div className="detail-view-section">
                <div className="detail-view">
                  <InputNumberField
                    name={'Density during loading (kg/l)'}
                    fieldName={'densityDuringLoading'}
                    fieldValue={densityDuringLoading}
                    required={false}
                    error={getErrorMessageForFormField(errors, 'densityDuringLoading')}
                    onChange={onFormInputChange}
                    readOnly={readOnly}
                  />
                </div>
                <div className="detail-view">
                  <InputNumberField
                    name={'Temperature during loading (\u00B0C)'}
                    fieldName={'temperatureDuringLoading'}
                    fieldValue={temperatureDuringLoading}
                    required={false}
                    error={getErrorMessageForFormField(errors, 'temperatureDuringLoading')}
                    onChange={onFormInputChange}
                    readOnly={readOnly}
                  />
                </div>
                <div className="detail-view">
                  <InputNumberField
                    name={'Barge measurement during loading (kg)'}
                    fieldName={'bargeMeasurementDuringLoading'}
                    fieldValue={bargeMeasurementDuringLoading}
                    required={false}
                    error={getErrorMessageForFormField(errors, 'bargeMeasurementDuringLoading')}
                    onChange={onFormInputChange}
                    readOnly={readOnly}
                  />
                </div>
                <div className="detail-view">
                  <InputDateField
                    name={'Arrival time'}
                    fieldName={'arrivalTime'}
                    fieldValue={arrivalTime}
                    error={getErrorMessageForFormField(errors, 'arrivalTime')}
                    required={true}
                    onChange={onFormInputChange}
                    readOnly={readOnly}
                  />
                </div>
                <div className="detail-view">
                  <InputDateField
                    name={'Release time lab'}
                    fieldName={'releaseTimeLab'}
                    fieldValue={releaseTimeLab}
                    error={getErrorMessageForFormField(errors, 'releaseTimeLab')}
                    required={true}
                    pickerDisplayTop={true}
                    onChange={onFormInputChange}
                    readOnly={readOnly}
                  />
                </div>
                <div className="detail-view">
                  <InputDateField
                    name={'Date of attestation of unloading'}
                    fieldName={'dateOfAttestationOfUnloading'}
                    fieldValue={dateOfAttestationOfUnloading}
                    error={getErrorMessageForFormField(errors, 'dateOfAttestationOfUnloading')}
                    required={true}
                    pickerDisplayTop={true}
                    onChange={onFormInputChange}
                    readOnly={readOnly}
                  />
                </div>
              </div>
              <div className="detail-view-section">
                <div className="detail-view">
                  <DisplayField title={'Berth'} value={props.berthName} />
                </div>
                <div className="detail-view">
                  <DisplayField title={'Load station'} value={props.loadStationName} />
                </div>
                <div className="detail-view">
                  <DisplayField title={'Start unloading'} value={calculateStartUnloading(unloadingDetails)} />
                </div>
                <div className="detail-view">
                  <DisplayField title={'End unloading'} value={calculateEndUnloading(unloadingDetails)} />
                </div>
                <div className="detail-view">
                  <DisplayField title={'Total measured volume (L)'} value={roundIfDefined(totalMeasuredVolume, 0)} />
                </div>
                <div className="detail-view">
                  <DisplayField
                    title={'Average measured temperature (\u00B0C)'}
                    value={roundIfDefined(averageMeasuredTemperature, 1)}
                  />
                </div>
                <div className="detail-view">
                  <DisplayField title={'Total measured weight (kg)'} value={roundIfDefined(totalMeasuredWeight)} />
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel className="error">
            <div className="detail-view content">
              <div
                className={`detail-view-section ${getErrorMessageForFormField(errors, 'measurements') ? 'error' : ''}`}
              >
                {!isEmpty(measurements) ? (
                  <table className="table-cargo-operations-details">
                    <thead>
                      <tr>
                        <th>Tank *</th>
                        <th>Measured depth (cm)</th>
                        <th>Volume (L)</th>
                        <th>Temperature ({'\u00B0C'})</th>
                        <th>Density (kg/l)</th>
                        <th>Mass (kg)</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {measurements &&
                        measurements.map((measurement, index) => {
                          const density = calculateMeasurementDensity({
                            densityDuringLoading,
                            temperatureDuringLoading,
                            temperatureMeasurement: measurement.temperature
                          })

                          const mass = calculateMass({
                            volume: measurement.volume,
                            density
                          })

                          return (
                            <tr key={index}>
                              <td>
                                <InputSelectField
                                  fieldName={'tank'}
                                  fieldValue={measurement.tank}
                                  onChange={(value, field) => handleChangeMeasurements(value, field, index)}
                                  options={MEASUREMENTS_TANK_OPTIONS}
                                  error={getErrorMessageForFormField(errors, `measurements[${index}].tank`)}
                                  readOnly={readOnly}
                                />
                              </td>
                              <td>
                                <InputNumberField
                                  name={'measuredDepthCm'}
                                  fieldName={'measuredDepthCm'}
                                  fieldValue={measurement.measuredDepthCm}
                                  required={false}
                                  error={getErrorMessageForFormField(errors, `measurements[${index}].measuredDepthCm`)}
                                  onChange={(value, field) => handleChangeMeasurements(value, field, index)}
                                  fieldOnlyBoolean
                                  readOnly={readOnly}
                                />
                              </td>
                              <td>
                                <InputNumberField
                                  name={'volume'}
                                  fieldName={'volume'}
                                  fieldValue={measurement.volume}
                                  required={false}
                                  error={getErrorMessageForFormField(errors, `measurements[${index}].volume`)}
                                  onChange={(value, field) => handleChangeMeasurements(value, field, index)}
                                  fieldOnlyBoolean
                                  readOnly={readOnly}
                                />
                              </td>
                              <td>
                                <InputNumberField
                                  name={'temperature'}
                                  fieldName={'temperature'}
                                  fieldValue={measurement.temperature}
                                  required={false}
                                  error={getErrorMessageForFormField(errors, `measurements[${index}].temperature`)}
                                  onChange={(value, field) => handleChangeMeasurements(value, field, index)}
                                  fieldOnlyBoolean
                                  readOnly={readOnly}
                                />
                              </td>
                              <td>
                                <DisplayField
                                  title={'Bulk density (kg/l)'}
                                  value={roundIfDefined(density, 5)}
                                  fieldOnlyBoolean
                                />
                              </td>
                              <td>
                                <DisplayField title={'Mass (kg)'} value={roundIfDefined(mass, 0)} fieldOnlyBoolean />
                              </td>
                              <td>
                                <div className="buttons">
                                  {!readOnly && (
                                    <button
                                      className="btn delete-btn"
                                      onClick={e => handleDeleteMeasurements(e, index)}
                                    >
                                      Delete
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                    </tbody>
                  </table>
                ) : null}

                <div className="buttons table-buttons">
                  {!readOnly && (
                    <button className="btn add-btn" onClick={handleAddMeasurements}>
                      Add measurement
                    </button>
                  )}
                </div>
              </div>
              <div className="detail-view content columns auto-height">
                <div className="detail-view-section">
                  <h1>Slop tanks prior</h1>
                  <div className="detail-view">
                    <InputNumberField
                      name={'Starboard discharge (cm)'}
                      title={'Slop tank prior starboard discharge (cm)'}
                      fieldName={'priorStarboardDischargeCm'}
                      fieldValue={slobTanks ? slobTanks.priorStarboardDischargeCm : null}
                      required={false}
                      error={getErrorMessageForFormField(errors, `slobTanks.priorStarboardDischargeCm`)}
                      onChange={handleChangeSlobTanks}
                      readOnly={readOnly}
                      positiveNoDecimal
                    />
                  </div>
                  <div className="detail-view">
                    <InputNumberField
                      name={'Portside discharge (cm)'}
                      title={'Slop tank prior portside discharge (cm)'}
                      fieldName={'priorPortsideDischargeCm'}
                      fieldValue={slobTanks ? slobTanks.priorPortsideDischargeCm : null}
                      required={false}
                      error={getErrorMessageForFormField(errors, `slobTanks.priorPortsideDischargeCm`)}
                      onChange={handleChangeSlobTanks}
                      readOnly={readOnly}
                      positiveNoDecimal
                    />
                  </div>
                  <div className="detail-view">
                    <InputNumberField
                      name={'Center discharge (cm)'}
                      title={'Slop tank prior center discharge (cm)'}
                      fieldName={'priorCenterDischargeCm'}
                      fieldValue={slobTanks ? slobTanks.priorCenterDischargeCm : null}
                      required={false}
                      error={getErrorMessageForFormField(errors, `slobTanks.priorCenterDischargeCm`)}
                      onChange={handleChangeSlobTanks}
                      readOnly={readOnly}
                      positiveNoDecimal
                    />
                  </div>
                </div>
                <div className="detail-view-section">
                  <h1>Slop tanks after</h1>
                  <div className="detail-view">
                    <InputNumberField
                      name={'Starboard discharge (cm)'}
                      title={'Slop tank after starboard discharge (cm)'}
                      fieldName={'afterStarboardDischargeCm'}
                      fieldValue={slobTanks ? slobTanks.afterStarboardDischargeCm : null}
                      required={false}
                      error={getErrorMessageForFormField(errors, `slobTanks.afterStarboardDischargeCm`)}
                      onChange={handleChangeSlobTanks}
                      readOnly={readOnly}
                      positiveNoDecimal
                    />
                  </div>
                  <div className="detail-view">
                    <InputNumberField
                      name={'Portside discharge (cm)'}
                      title={'Slop tank after portside discharge (cm)'}
                      fieldName={'afterPortsideDischargeCm'}
                      fieldValue={slobTanks ? slobTanks.afterPortsideDischargeCm : null}
                      required={false}
                      error={getErrorMessageForFormField(errors, `slobTanks.afterPortsideDischargeCm`)}
                      onChange={handleChangeSlobTanks}
                      readOnly={readOnly}
                      positiveNoDecimal
                    />
                  </div>
                  <div className="detail-view">
                    <InputNumberField
                      name={'Center discharge (cm)'}
                      title={'Sloptank after center discharge (cm)'}
                      fieldName={'afterCenterDischargeCm'}
                      fieldValue={slobTanks ? slobTanks.afterCenterDischargeCm : null}
                      required={false}
                      error={getErrorMessageForFormField(errors, `slobTanks.afterCenterDischargeCm`)}
                      onChange={handleChangeSlobTanks}
                      readOnly={readOnly}
                      positiveNoDecimal
                    />
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel>
            <div className="detail-view content">
              <div
                className={`detail-view-section ${
                  getErrorMessageForFormField(errors, 'unloadingDetails') ? 'error' : ''
                }`}
              >
                {!isEmpty(unloadingDetails) ? (
                  <table className="table-cargo-operations-details">
                    <thead>
                      <tr>
                        <th>Tank</th>
                        <th>Start time *</th>
                        <th>End time *</th>
                        <th>Start level (MT)</th>
                        <th>End level (MT)</th>
                        <th>Difference levels (MT)</th>
                        <th>Average unload speed (MT/h)</th>
                        <th>Final sample taken</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {unloadingDetails &&
                        unloadingDetails.map((unloadingDetail, index) => {
                          // the date picker has a height of 5 rows
                          const pickerDisplayTop = index > 3 && index >= unloadingDetails.length - 5

                          return (
                            <tr key={index}>
                              <td>
                                <InputTextField
                                  name={'Tank'}
                                  fieldName={'tank'}
                                  fieldValue={unloadingDetail.tank}
                                  error={getErrorMessageForFormField(errors, `unloadingDetails[${index}].tank`)}
                                  required={false}
                                  onChange={(value, field) => handleChangeUnloadingDetails(value, field, index)}
                                  fieldOnlyBoolean
                                  readOnly={readOnly}
                                />
                              </td>
                              <td>
                                <InputDateField
                                  name={'Start time'}
                                  fieldName={'startTime'}
                                  fieldValue={unloadingDetail.startTime}
                                  error={getErrorMessageForFormField(errors, `unloadingDetails[${index}].startTime`)}
                                  required={false}
                                  pickerDisplayTop={pickerDisplayTop}
                                  onChange={(value, field) => handleChangeUnloadingDetails(value, field, index)}
                                  fieldOnlyBoolean
                                  readOnly={readOnly}
                                />
                              </td>
                              <td>
                                <InputDateField
                                  name={'End time'}
                                  fieldName={'endTime'}
                                  fieldValue={unloadingDetail.endTime}
                                  error={getErrorMessageForFormField(errors, `unloadingDetails[${index}].endTime`)}
                                  required={false}
                                  pickerDisplayTop={pickerDisplayTop}
                                  onChange={(value, field) => handleChangeUnloadingDetails(value, field, index)}
                                  fieldOnlyBoolean
                                  readOnly={readOnly}
                                />
                              </td>
                              <td>
                                <InputNumberField
                                  name={'Start level'}
                                  fieldName={'startLevel'}
                                  fieldValue={unloadingDetail.startLevel}
                                  required={false}
                                  error={getErrorMessageForFormField(errors, `unloadingDetails[${index}].startLevel`)}
                                  onChange={(value, field) => handleChangeUnloadingDetails(value, field, index)}
                                  fieldOnlyBoolean
                                  readOnly={readOnly}
                                />
                              </td>
                              <td>
                                <InputNumberField
                                  name={'End level'}
                                  fieldName={'endLevel'}
                                  fieldValue={unloadingDetail.endLevel}
                                  required={false}
                                  error={getErrorMessageForFormField(errors, `unloadingDetails[${index}].endLevel`)}
                                  onChange={(value, field) => handleChangeUnloadingDetails(value, field, index)}
                                  fieldOnlyBoolean
                                  readOnly={readOnly}
                                />
                              </td>
                              <td>
                                <DisplayField
                                  title={'Difference in levels'}
                                  value={roundIfDefined(
                                    calculateLevelDifference(unloadingDetail.startLevel, unloadingDetail.endLevel)
                                  )}
                                  fieldOnlyBoolean
                                />
                              </td>
                              <td>
                                <DisplayField
                                  title={'Average unload speed'}
                                  value={roundIfDefined(calculateAverageUnloadSpeed(unloadingDetail), 1)}
                                  fieldOnlyBoolean
                                />
                              </td>
                              <td className="checkbox">
                                <CheckBoxField
                                  name={'Final sample taken'}
                                  fieldName={'finalSampleTaken'}
                                  fieldValue={unloadingDetail.finalSampleTaken}
                                  onChange={(value, field) => handleChangeUnloadingDetails(value, field, index)}
                                  fieldOnlyBoolean
                                  readOnly={readOnly}
                                />
                              </td>
                              <td>
                                {!readOnly && (
                                  <div className="buttons">
                                    <button
                                      className="btn delete-btn"
                                      onClick={e => handleDeleteUnloadingDetails(e, index)}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                    </tbody>
                  </table>
                ) : null}
                <div className="buttons table-buttons">
                  {!readOnly && (
                    <button className="btn add-btn" onClick={handleAddUnloadingDetails}>
                      Add unloading details
                    </button>
                  )}
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel>
            <div className="detail-view content columns">
              <div className="detail-view-section">
                <div className="detail-view">
                  <h1>Discharge point</h1>
                  <InputSelectField
                    title={'Empty barge inspection'}
                    fieldName={'emptyBargeInspection'}
                    fieldValue={emptyBargeInspection}
                    onChange={onFormInputChange}
                    options={['', 'OK', 'NOT OK']}
                    error={false}
                    readOnly={readOnly}
                  />
                </div>
                <div className="detail-view">
                  <InputSelectField
                    title={'Discharge point closed inspection'}
                    fieldName={'dischargePointClosedInspection'}
                    fieldValue={dischargePointClosedInspection}
                    onChange={onFormInputChange}
                    options={['', 'OK', 'NOT OK']}
                    error={false}
                    readOnly={readOnly}
                  />
                </div>
                <div className="detail-view">
                  <InputTextAreaField
                    name={'Comment'}
                    fieldName={'afterUnloadingComment'}
                    fieldValue={afterUnloadingComment}
                    error={false}
                    required={false}
                    onChange={onFormInputChange}
                    readOnly={readOnly}
                  />
                </div>
              </div>
              <div className="detail-view-section">
                <h1>Seals</h1>
                {seals &&
                  seals.map((seal, index) => (
                    <div className="detail-view double-view seals-section" key={index}>
                      <InputTextField
                        name={'ID'}
                        fieldName={'seal'}
                        fieldValue={seals[index]}
                        error={false}
                        required={false}
                        onChange={value => handleChangeSeal(value, index)}
                        readOnly={readOnly}
                      />

                      {!readOnly && (
                        <button className="btn delete-btn seal-delete-btn" onClick={e => handleDeleteSeal(e, index)}>
                          Delete
                        </button>
                      )}
                    </div>
                  ))}

                <div className="buttons">
                  <button className="btn add-btn" onClick={handleAddSeal}>
                    Add Seal
                  </button>
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel>
            <div className="detail-view content columns">
              <div className="detail-view-section">
                <h1>General</h1>

                <div className="detail-view">
                  <DisplayField title={'Date of arrival'} value={dateOfArrival} />
                </div>
                <div className="detail-view">
                  <DisplayField title={'Vessel name'} value={shipName} />
                </div>
                <div className="detail-view">
                  <DisplayField title={'IMO number'} value={imo || '-'} />
                </div>
                <div className="detail-view">
                  <DisplayField title={'ENI number'} value={eni || '-'} />
                </div>
                <div className="detail-view">
                  <DisplayField title={'P number'} value={pNumber} />
                </div>
                <div className="detail-view">
                  <DisplayField title={'Product'} value={product} />
                </div>
              </div>

              <div className="detail-view-section">
                <h1>Loading</h1>

                <div className="detail-view">
                  <DisplayField title={'Bill of loading weight (kg)'} value={billOfLoadingWeight} />
                </div>
                <div className="detail-view">
                  <DisplayField title={'Barge Measurement during loading (kg)'} value={bargeMeasurementDuringLoading} />
                </div>
                <div className="detail-view">
                  <DisplayField
                    title={'Weight difference at loading (%)'}
                    value={weightDifferenceAtLoading}
                    className={STATUS_CLASS_NAME[weightDifferenceAtLoadingOk]}
                  />
                </div>
              </div>

              <div className="detail-view-section">
                <h1>Izegem</h1>

                <div className="detail-view">
                  <DisplayField title={'Total measured volume (l)'} value={totalMeasuredVolume} />
                </div>
                <div className="detail-view">
                  <DisplayField title={'Average measured temperature (\u00B0C)'} value={averageMeasuredTemperature} />
                </div>
                <div className="detail-view">
                  <DisplayField title={'Total measured weight (kg)'} value={totalMeasuredWeight} />
                </div>
                <div className="detail-view">
                  <DisplayField
                    title={'Weight difference total (%)'}
                    value={weightDifference}
                    className={STATUS_CLASS_NAME[weightDifferenceOk]}
                  />
                </div>
                <div className="detail-view">
                  <DisplayField
                    title={'Weight difference at Izegem (%)'}
                    value={weightDifferenceIzegem}
                    className={STATUS_CLASS_NAME[weightDifferenceIzegemOk]}
                  />
                </div>
              </div>

              <div className="detail-view-section">
                <h1>Status</h1>

                <div className="detail-view">
                  <DisplayField title={'Status'} value={statusOkText} className={STATUS_CLASS_NAME[statusOk]} />
                </div>

                <div className="detail-view">
                  <InputTextAreaField
                    name={'Comment'}
                    fieldName={'statusComment'}
                    fieldValue={statusComment}
                    error={false}
                    required={false}
                    onChange={onFormInputChange}
                    readOnly={readOnly}
                  />
                </div>

                {!readOnly && approved === true
                  ? [
                      <div className="detail-view" key={approvedBy}>
                        <DisplayField title={'Approved by'} value={approvedBy} />
                      </div>,
                      <div className="detail-view" key={approvedTimestamp}>
                        <DisplayField
                          title={'Approved at'}
                          value={moment(approvedTimestamp).format(startDateFormatString)}
                        />
                      </div>
                    ]
                  : null}

                <div className="group">
                  {!readOnly && (
                    <button
                      className="btn"
                      title="Send an email requesting to approve releasing this vessel"
                      onClick={handleSendCargoOperationsEmail}
                    >
                      Send email
                    </button>
                  )}
                </div>

                {!readOnly && !statusOk ? (
                  <button
                    className="btn approve"
                    title="Enter the person who approved, and mark the vessel as approved"
                    onClick={toggleApproveVesselPopup}
                  >
                    Mark approved
                  </button>
                ) : null}

                <div className="group">
                  {!readOnly && (
                    <button
                      className="btn"
                      title="Download a Word document with the cargo manifest for this visit"
                      onClick={() => {
                        setValidateManifest(true)
                        handleTabIndexOnSave(values, validateFormEntriesForDownload)
                        handleSubmit()
                      }}
                    >
                      Download Cargo Manifest
                    </button>
                  )}
                </div>
              </div>
            </div>
          </TabPanel>
        </Tabs>
      </div>
      <div className="footer">
        {!isEmpty(errors) ? (
          <ErrorMessage message={'Please make sure that you filled in all fields correctly'} />
        ) : null}
        <Modal.Buttons>
          {!readOnly && (
            <div className="btn" onClick={props.closeModal}>
              <span>Cancel</span>
            </div>
          )}
          {!readOnly && !createNew ? (
            <div className="no-btn btn" onClick={confirmDeleteAction}>
              <span>Delete</span>
            </div>
          ) : null}
          {!readOnly && !!values && values.terminalVisitId ? (
            <div
              className="yes-btn btn"
              onClick={() => {
                setValidateManifest(false)
                handleTabIndexOnSave(values, validateCargoOperations)
                handleSubmit()
              }}
            >
              <span>Save</span>
            </div>
          ) : null}
        </Modal.Buttons>
      </div>
    </>
  )

  async function fetchCargoOperationsDetails() {
    try {
      const cargoOperationsDetails = await fetchCargoOperationsDetailsFromBackendRest(
        props.identifiers,
        apiUrlSuffix,
        props.authToken,
        props.customFetch
      )

      // backend gives an empty object back instead of 404 if it doesn't exist
      setCreateNew(false)
      setAllValues(insertStaticInformation(cargoOperationsDetails))
    } catch (err) {
      // 404 not found is no problem, in that case we simply don't have a document yet.
      // Let's create one.
      setCreateNew(true)
      setAllValues(insertStaticInformation({}))

      if (err.status !== 404) {
        handleFetchError(err)
      }
    }
  }

  function insertStaticInformation(cargoOperationsDetails) {
    return {
      ...cargoOperationsDetails,
      terminalVisitId: props.identifiers.terminalVisitId,
      orderId: props.identifiers.orderId
    }
  }

  /**
   * When the value of a form input field changes
   */
  function onFormInputChange(value, field) {
    const cargoOperationsDetailsCopy = cloneDeep(values)
    set(cargoOperationsDetailsCopy, field, value)
    setAllValues(cargoOperationsDetailsCopy)
  }

  function handleAddUnloadingDetails(e) {
    e.preventDefault()
    const cargoOperationsDetailsCopy = cloneDeep(values)
    cargoOperationsDetailsCopy.unloadingDetails = (cargoOperationsDetailsCopy.unloadingDetails || []).concat([{}])
    setAllValues(cargoOperationsDetailsCopy)
  }

  function handleDeleteUnloadingDetails(e, index) {
    e.preventDefault()
    const cargoOperationsDetailsCopy = cloneDeep(values)
    cargoOperationsDetailsCopy.unloadingDetails.splice(index, 1)
    setAllValues(cargoOperationsDetailsCopy)
  }

  function handleChangeUnloadingDetails(value, field, index) {
    const cargoOperationsDetailsCopy = cloneDeep(values)
    cargoOperationsDetailsCopy.unloadingDetails[index][field] = value
    setAllValues(cargoOperationsDetailsCopy)
  }

  function handleChangeSlobTanks(value, field) {
    const cargoOperationsDetailsCopy = cloneDeep(values)

    if (!cargoOperationsDetailsCopy.slobTanks) {
      cargoOperationsDetailsCopy.slobTanks = {}
    }

    cargoOperationsDetailsCopy.slobTanks[field] = value
    setAllValues(cargoOperationsDetailsCopy)
  }

  function handleChangeMeasurements(value, field, index) {
    const cargoOperationsDetailsCopy = cloneDeep(values)
    cargoOperationsDetailsCopy.measurements[index][field] = value
    setAllValues(cargoOperationsDetailsCopy)
  }

  function handleDeleteMeasurements(e, index) {
    e.preventDefault()
    const cargoOperationsDetailsCopy = cloneDeep(values)
    cargoOperationsDetailsCopy.measurements.splice(index, 1)
    setAllValues(cargoOperationsDetailsCopy)
  }

  function handleAddMeasurements(e) {
    e.preventDefault()
    const cargoOperationsDetailsCopy = cloneDeep(values)
    cargoOperationsDetailsCopy.measurements = (cargoOperationsDetailsCopy.measurements || []).concat([{}])
    setAllValues(cargoOperationsDetailsCopy)
  }

  function handleChangeSeal(value, index) {
    const cargoOperationsDetailsCopy = cloneDeep(values)
    cargoOperationsDetailsCopy.seals[index] = value
    setAllValues(cargoOperationsDetailsCopy)
  }

  function handleDeleteSeal(e, index) {
    e.preventDefault()
    const cargoOperationsDetailsCopy = cloneDeep(values)
    cargoOperationsDetailsCopy.seals.splice(index, 1)
    setAllValues(cargoOperationsDetailsCopy)
  }

  function handleAddSeal(e) {
    e.preventDefault()
    const cargoOperationsDetailsCopy = cloneDeep(values)
    cargoOperationsDetailsCopy.seals = (cargoOperationsDetailsCopy.seals || []).concat('')
    setAllValues(cargoOperationsDetailsCopy)
  }

  /**
   * Validates that form contains all required fields for generating cargo manifest on backend.
   */
  function validateFormEntriesForDownload() {
    // validate all the formfields
    const formErrors = validateCargoOperations(values)
    // additionally also make sure the billOfLoadingWeight is filled in, this is specifically required when downloading the manifest, otherweise not.
    if (checkIfAFieldIsEmpty(values.billOfLoadingWeight)) {
      formErrors.billOfLoadingWeight = 'Required'
    }

    // additionally also make sure the billOfLoadingDate is filled in, this is specifically required when downloading the manifest, otherweise not.
    if (checkIfAFieldIsEmpty(values.billOfLoadingDate)) {
      formErrors.billOfLoadingDate = 'Required'
    }

    // additionally also make sure the arrivalTime is filled in, this is specifically required when downloading the manifest, otherweise not.
    if (checkIfAFieldIsEmpty(values.arrivalTime)) {
      formErrors.arrivalTime = 'Required'
    }

    // additionally also make sure the releaseTimeLab is filled in, this is specifically required when downloading the manifest, otherweise not.
    if (checkIfAFieldIsEmpty(values.releaseTimeLab)) {
      formErrors.releaseTimeLab = 'Required'
    }

    // additionally also make sure the dateOfAttestationOfUnloading is filled in, this is specifically required when downloading the manifest, otherweise not.
    if (checkIfAFieldIsEmpty(values.dateOfAttestationOfUnloading)) {
      formErrors.dateOfAttestationOfUnloading = 'Required'
    }

    // Make sure there is atleast one unloadingDetails is filled in
    if (!values.unloadingDetails || (values.unloadingDetails && values.unloadingDetails.length === 0)) {
      formErrors.unloadingDetails = 'Required'
    } else if (values.unloadingDetails.length > 0) {
      const hasEmptyDetails = values.unloadingDetails.some(details => isEmpty(details))
      if (hasEmptyDetails) {
        formErrors.unloadingDetails = 'Required'
      }
    }

    // Make sure there is atleast one measurement filled in. If not, then set the measurementError object to be a array with a empty object.
    // That way the form knows there's an error connected to the measurements.
    const measurementErrors = isEmpty(values.measurements) ? [{}] : undefined
    if (measurementErrors) {
      formErrors.measurements = measurementErrors
    }

    return formErrors
  }

  function handleClosePopup() {
    if (props.closeModal) {
      props.closeModal()
    }
  }

  function setValidateManifest(isCargoManifest) {
    validateManifest.current = isCargoManifest
  }

  function validate(val) {
    const formErrors = validateManifest.current ? validateFormEntriesForDownload() : validateCargoOperations(val)

    if (!hasBeenValidatedOnce) {
      setHasBeenValidatedOnce(true)
      const tabIndex = getTabIndexFromErrors(formErrors)
      setTabIndex(tabIndex)
    }

    return formErrors
  }

  async function onValidSubmit() {
    if (validateManifest.current) {
      handleDownloadCargoManifest()
      handleClosePopup()
      return
    }
    await save(values)
    handleClosePopup()
  }

  function handleTabIndexOnSave(formValues, validationFunction) {
    const formErrors = validationFunction(formValues)
    const tabIndex = getTabIndexFromErrors(formErrors)
    setTabIndex(tabIndex)
  }

  function getTabIndexFromErrors(formErrors) {
    const mainTabFields = [
      'billOfLoadingDate',
      'arrivalTime',
      'releaseTimeLab',
      'dateOfAttestationOfUnloading',
      'billOfLoadingWeight',
      'deliveredSamples',
      'densityDuringLoading',
      'bargeMeasurementDuringLoading'
    ]
    if (!isEmpty(formErrors)) {
      let firstTabHasErrors = false
      Object.entries(formErrors).forEach(([key]) => {
        if (mainTabFields.includes(key)) {
          firstTabHasErrors = true
        }
      })
      if (firstTabHasErrors) {
        return 0
      }
    }
    // if there are errors in the measurements or the slobtanks, this is in the second tab
    if (!isEmpty(formErrors.measurements) || !isEmpty(formErrors.slobTanks)) {
      return 1
    }
    // if there are errors in the unloadingDetails or the slobtanks, this is in the third tab
    if (!isEmpty(formErrors.unloadingDetails)) {
      return 2
    }
    // otherwise there are errors in the first tab
    return 0
  }

  async function save(saveValues) {
    const cargoDetails = cloneDeep(saveValues)

    // remove empty seal IDs from form entry sent to backend
    if (!isEmpty(cargoDetails.seals)) {
      cargoDetails.seals = cargoDetails.seals.filter(s => s !== '')
    }

    try {
      if (cargoDetails._id) {
        const updatedCargoDetails = await updateCargoOperationsDetailsInBackendRest(
          props.identifiers,
          apiUrlSuffix,
          cargoDetails,
          props.authToken,
          props.customFetch
        )
        setAllValues(updatedCargoDetails)
      } else {
        const insertedCargoDetails = await insertCargoOperationsDetailsInBackendRest(
          props.identifiers,
          apiUrlSuffix,
          cargoDetails,
          props.authToken,
          props.customFetch
        )
        setAllValues(insertedCargoDetails) // mostly important to fill in the _id that the document got after insert
      }
    } catch (err) {
      handleFetchError(err, 'Error: Failed to add or update the cargo operation details')
    }
  }

  function confirmDeleteAction() {
    confirmDeletePopup({
      itemName: 'the cargo operations details',
      onConfirm: handleDeleteCargoOperationsDetails,
      alert: true
    })
  }

  async function handleDeleteCargoOperationsDetails() {
    try {
      if (values && values.terminalVisitId) {
        await deleteCargoOperationsDetailsFromBackendRest(
          props.identifiers,
          apiUrlSuffix,
          props.authToken,
          props.customFetch
        )
      }
    } catch (err) {
      handleFetchError(err, 'Error: Failed to delete cargo operation')
    }

    handleClosePopup()
  }

  function toggleApproveVesselPopup() {
    setApproveVesselPopupActive(approveVesselPopupActive => !approveVesselPopupActive)
  }

  /**
   * @param {string} approvedBy
   */
  async function approveBlockedVessel(approvedBy) {
    const cargoOperationsDetailsCopy = Object.assign({}, values, {
      approved: true,
      approvedBy,
      approvedTimestamp: Date.now()
    })

    await setAllValues(cargoOperationsDetailsCopy)

    // save to make sure we don't lose unsaved changes
    // We pass cargoOperationsDetailsCopy here to save(), because after await setAllValues
    // we're still having a reference to an old version of save() here,
    // which would reference an old, outdated version of `values` if not passed here.
    await save(cargoOperationsDetailsCopy)

    toggleApproveVesselPopup()
  }

  function handleSendCargoOperationsEmail() {
    const summary = calculateSummary(props.order, values, props.ship)

    if (summary.allDifferencesOk === undefined || summary.shipName === undefined) {
      alertPopup({
        title: 'Blocked vessel',
        message: (
          <div>
            <p>Cannot send an email: status is not known. Please fill in all required data first.</p>
          </div>
        ),
        okClassName: 'btn'
      })

      return
    }

    const statusOkText = summary.statusOk === true ? 'released' : summary.statusOk === false ? 'blocked' : undefined

    const title = `${capitalize(statusOkText)} vessel email`

    confirmPopup({
      title,
      message: (
        <div>
          <p>
            Are you sure you want to send an email about the {summary.shipName} being {statusOkText}?
          </p>
          <p>The email will be send to the following addresses:</p>
          <ul>
            {CARGO_OPERATIONS_EMAIL_ADDRESSES.map(email => {
              return <li key={email}>{email}</li>
            })}
          </ul>
        </div>
      ),
      okText: 'Yes, send email',
      onConfirm: async () => {
        try {
          // save to make sure we don't lose unsaved changes
          await save(values)

          await sendCargoOperationsEmail(
            props.identifiers,
            apiUrlSuffix,
            CARGO_OPERATIONS_EMAIL_ADDRESSES,
            CARGO_OPERATIONS_REPLY_TO_EMAIL,
            summary,
            props.authToken,
            props.customFetch
          )

          toast.success('Email has been sent successfully')
        } catch (err) {
          handleFetchError(err, 'Error: Failed to send email')
        }
      },
      confirmClassName: 'btn save-btn',
      cancelClassName: 'btn no-btn'
    })
  }

  async function handleDownloadCargoManifest() {
    const formErrors = validateFormEntriesForDownload()
    const tabIndex = getTabIndexFromErrors(formErrors)
    if (!isEmpty(formErrors)) {
      setTabIndex(tabIndex)
      return
    }

    // save to make sure we don't lose unsaved changes
    await save(values)

    const shipName = (props.ship && props.ship.name) || ''
    const arrivalTime = (values && values.arrivalTime) || undefined
    const arrivalTimeStr = arrivalTime ? moment(arrivalTime).format('YYYY-MM-DD HHmm') : ''
    const fileName = `Cargo manifest ${shipName} ${arrivalTimeStr}.docx`

    try {
      await downloadCargoManifest(props.identifiers, apiUrlSuffix, fileName, props.authToken, props.customFetch)
    } catch (err) {
      handleFetchError(err)
    }
  }
}

CargoOperationsDetailsCargill.propTypes = {
  identifiers: PropTypes.shape({
    terminalId: PropTypes.string.isRequired,
    terminalVisitId: PropTypes.string.isRequired,
    orderId: PropTypes.string.isRequired
  }).isRequired,
  order: PropTypes.object.isRequired,
  ship: PropTypes.object.isRequired,
  berthName: PropTypes.string,
  loadStationName: PropTypes.string,
  authToken: PropTypes.string,
  customFetch: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired
}

export default React.memo(CargoOperationsDetailsCargill)
